/** O pop-up de importação por planilha das abas de Ajustes (2026-10-20).
 *
 *  O painel de importação ficava entre o formulário e a lista, e era mais
 *  uma coisa na frente do que já está cadastrado. Agora ele abre por um
 *  botão ao lado de "Novo …", com o guia das colunas da planilha.
 *
 *  **O painel fica MONTADO com o pop-up fechado** (só escondido): é ele
 *  que acompanha a importação em andamento e manda a aba recarregar a
 *  lista quando ela termina. Desmontar ao fechar faria a pessoa enviar o
 *  arquivo, fechar, e não ver o cadastro novo até trocar de aba. Com ele
 *  montado, o botão de abrir diz "Importando…" enquanto a fila trabalha
 *  (o sino avisa do fim, como antes).
 *
 *  Fecha clicando fora ou no Esc: aqui não há nada digitado a perder. */

import { FileSpreadsheet, LoaderCircle, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";

import type { CatalogImportKind } from "@/shared/api/api-client";

import { CatalogImportPanel, type CurrentSheet } from "./CatalogImportPanel";
import { ImportColumnGuide } from "./import-column-guide";

const DIALOG_TITLE: Record<CatalogImportKind, string> = {
  catalog: "Importar canais e veículos por planilha",
  publishers: "Importar publicadores por planilha",
  company: "Importar configuração por planilha",
  rules: "Importar premissas por planilha",
  themes: "Importar temas e subtemas por planilha"
};

type Props = {
  token: string;
  kind?: CatalogImportKind;
  companyName?: string;
  currentSheet?: CurrentSheet;
  onFinished: () => void;
};

/** O botão e o pop-up juntos: a aba põe UM elemento no cabeçalho. */
export function CatalogImportButton({
  token,
  kind = "catalog",
  companyName,
  currentSheet,
  onFinished
}: Props) {
  const [open, setOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const onActiveChange = useCallback((active: boolean) => setImporting(active), []);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !event.defaultPrevented) setOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  return (
    <>
      <button type="button" className="secondary-button" onClick={() => setOpen(true)}>
        {importing ? <LoaderCircle className="spin" size={16} /> : <FileSpreadsheet size={16} />}
        {importing ? "Importando…" : "Importar planilha"}
      </button>
      {createPortal(
        <div
          className="modal-overlay settings-dialog-overlay"
          hidden={!open}
          onClick={() => setOpen(false)}
        >
          <div
            className="modal-container ajustes-panel settings-dialog is-wide"
            role="dialog"
            aria-modal="true"
            aria-label={DIALOG_TITLE[kind]}
            onClick={event => event.stopPropagation()}
          >
            <header className="modal-header">
              <div>
                <span className="eyebrow">IMPORTAÇÃO</span>
                <h2>{DIALOG_TITLE[kind]}</h2>
              </div>
              <button
                type="button"
                className="icon-button"
                onClick={() => setOpen(false)}
                aria-label="Fechar"
              >
                <X size={18} />
              </button>
            </header>
            <div className="modal-body settings-dialog-body">
              <CatalogImportPanel
                token={token}
                kind={kind}
                companyName={companyName}
                currentSheet={currentSheet}
                onFinished={onFinished}
                inDialog
                guide={<ImportColumnGuide kind={kind} />}
                onActiveChange={onActiveChange}
              />
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
